import { useCallback, useEffect, useMemo, useState } from 'react';
// Subpath import, never the `@modules/firebase-core` barrel: the barrel pulls
// WebPlacementService → webplacements.types, which uses `import.meta.env` and
// breaks every jest consumer of this hook (StrategyFormDialog.test.tsx).
import { BlockSubtypeService } from '@modules/firebase-core/services/BlockSubtypeService';
import type { BlockSubtype, BlockSubtypeInput } from '@modules/shared-types';

export interface UseBlockSubtypesResult {
  subtypes: BlockSubtype[];
  enabledSubtypes: BlockSubtype[];
  loading: boolean;
  error: Error | null;
  createSubtype: (input: BlockSubtypeInput) => Promise<BlockSubtype>;
  updateSubtype: (id: string, patch: Partial<BlockSubtypeInput>) => Promise<BlockSubtype>;
  deleteSubtype: (id: string) => Promise<void>;
}

/** Real-time project catalog hook. UI composition remains in B5-I3. */
export function useBlockSubtypes(projectId: string): UseBlockSubtypesResult {
  const service = useMemo(() => new BlockSubtypeService(projectId), [projectId]);
  const [subtypes, setSubtypes] = useState<BlockSubtype[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    setSubtypes([]);
    setLoading(true);
    setError(null);
    return service.onCollectionSnapshot((next) => {
      setSubtypes(next);
      setLoading(false);
    }, (nextError) => {
      setError(nextError);
      setLoading(false);
    });
  }, [service]);

  const createSubtype = useCallback(
    (input: BlockSubtypeInput) => service.create(input),
    [service],
  );
  const updateSubtype = useCallback(
    (id: string, patch: Partial<BlockSubtypeInput>) => service.update(id, patch),
    [service],
  );
  const deleteSubtype = useCallback((id: string) => service.delete(id), [service]);

  return {
    subtypes,
    enabledSubtypes: useMemo(() => subtypes.filter((item) => item.enabled), [subtypes]),
    loading,
    error,
    createSubtype,
    updateSubtype,
    deleteSubtype,
  };
}
